import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Copy, Check, Terminal, Key } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/docs/api')({
  component: ApiReferencePage,
});

const endpoints = [
  {
    method: 'POST',
    path: '/api/v1/repositories',
    title: 'Create Repository',
    description: 'Add a new repository for analysis',
    category: 'Repositories',
  },
  {
    method: 'GET',
    path: '/api/v1/repositories',
    title: 'List Repositories',
    description: 'Get all repositories in your workspace',
    category: 'Repositories',
  },
  {
    method: 'POST',
    path: '/api/v1/repositories/:id/analyze',
    title: 'Analyze Repository',
    description: 'Trigger analysis on a repository',
    category: 'Analysis',
  },
  {
    method: 'GET',
    path: '/api/v1/repositories/:id/features',
    title: 'Get Features',
    description: 'Retrieve extracted features from analysis',
    category: 'Analysis',
  },
  {
    method: 'POST',
    path: '/api/v1/repositories/:id/prd',
    title: 'Generate PRD',
    description: 'Generate a product requirement document',
    category: 'PRD',
  },
  {
    method: 'GET',
    path: '/api/v1/repositories/:id/prd',
    title: 'Get PRD',
    description: 'Retrieve generated PRD content',
    category: 'PRD',
  },
  {
    method: 'GET',
    path: '/api/v1/repositories/:id/security',
    title: 'Security Report',
    description: 'Get security scan results',
    category: 'Security',
  },
];

function CodeBlock({ code, language: _language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 rounded-md bg-zinc-800 hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-zinc-400" />}
      </button>
    </div>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-green-500/10 text-green-500 border-green-500/20',
    POST: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    PUT: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    DELETE: 'bg-red-500/10 text-red-500 border-red-500/20',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold border ${colors[method] || ''}`}>
      {method}
    </span>
  );
}

function ApiReferencePage() {
  const categories = [...new Set(endpoints.map(e => e.category))];

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <div className="mb-8">
            <Link to="/docs" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Documentation
            </Link>
          </div>

          {/* Header */}
          <div className="mb-16">
            <Badge variant="secondary" className="mb-4">API Reference</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">ForgeNetwork API</h1>
            <p className="text-xl text-muted-foreground max-w-3xl">
              Integrate ForgeNetwork into your development workflow with our RESTful API.
              Automate repository analysis, feature extraction, and PRD generation.
            </p>
          </div>

          {/* Quick Start */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Quick Start</h2>
            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-primary" />
                    <CardTitle>Authentication</CardTitle>
                  </div>
                  <CardDescription>
                    All API requests require authentication via Bearer token
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CodeBlock code={`curl -X GET https://api.forgenetwork.pro/v1/repositories \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    <CardTitle>Base URL</CardTitle>
                  </div>
                  <CardDescription>
                    All API endpoints are relative to this base URL
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                    https://api.forgenetwork.pro/v1
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    API keys can be generated in your{' '}
                    <Link to="/settings" className="text-primary hover:underline">
                      dashboard settings
                    </Link>.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Rate Limits */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Rate Limits</h2>
            <Card>
              <CardContent className="pt-6">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-primary">100</div>
                    <div className="text-sm text-muted-foreground">requests/minute</div>
                    <div className="text-xs text-muted-foreground mt-1">Free tier</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-primary">1,000</div>
                    <div className="text-sm text-muted-foreground">requests/minute</div>
                    <div className="text-xs text-muted-foreground mt-1">Pro tier</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-primary">10,000</div>
                    <div className="text-sm text-muted-foreground">requests/minute</div>
                    <div className="text-xs text-muted-foreground mt-1">Enterprise</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Endpoints */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Endpoints</h2>
            <Tabs defaultValue={categories[0]} className="w-full">
              <TabsList className="mb-6">
                {categories.map(category => (
                  <TabsTrigger key={category} value={category}>
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
              {categories.map(category => (
                <TabsContent key={category} value={category}>
                  <div className="space-y-4">
                    {endpoints
                      .filter(e => e.category === category)
                      .map((endpoint, idx) => (
                        <Card key={idx}>
                          <CardHeader>
                            <div className="flex items-center gap-3">
                              <MethodBadge method={endpoint.method} />
                              <code className="text-sm font-mono">{endpoint.path}</code>
                            </div>
                            <CardTitle className="text-lg mt-2">{endpoint.title}</CardTitle>
                            <CardDescription>{endpoint.description}</CardDescription>
                          </CardHeader>
                        </Card>
                      ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </section>

          {/* SDKs */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">SDKs & Libraries</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Node.js / TypeScript</CardTitle>
                  <CardDescription>Official SDK for Node.js applications</CardDescription>
                </CardHeader>
                <CardContent>
                  <CodeBlock code="npm install @forgenetwork/sdk" />
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Python</CardTitle>
                  <CardDescription>Official SDK for Python applications</CardDescription>
                </CardHeader>
                <CardContent>
                  <CodeBlock code="pip install forgenetwork" />
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow opacity-60">
                <CardHeader>
                  <CardTitle className="text-lg">Go</CardTitle>
                  <CardDescription>Coming soon</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted rounded-lg p-4 text-sm text-muted-foreground">
                    In development
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* CTA */}
          <section>
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                  Create an account to get your API key and start integrating ForgeNetwork.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button asChild size="lg">
                    <Link to="/register">Get API Key</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/contact" search={{ type: 'support' }}>Contact Support</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
